import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { OverlayEventDetail } from '@ionic/core';
import { NEW_TASK_MODAL, TASK_MODAL } from '@lyceo/shared/plan/util';
import { TaskType } from '@lyceo/shared/plan/util-models';
import { IonicComponentRef } from '@lyceo/shared/util-models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { exhaustMap, map } from 'rxjs';
import { PlanPageActions } from '../plan/plan.actions';
import { PlanFacade } from '../plan/plan.facade';
import {
  NewTaskModalActions,
  TaskModalActions,
  TaskPageActions,
} from './task.actions';
import { HomiSettingsFacade } from '@lyceo/shared/homi-settings/data-access';

@Injectable()
export class TaskModalEffects {
  private readonly actions$ = inject(Actions);
  private readonly planFacade: PlanFacade = inject(PlanFacade);
  private readonly homiSettingsFacade: HomiSettingsFacade =
    inject(HomiSettingsFacade);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly newTaskModal = inject<IonicComponentRef>(NEW_TASK_MODAL);
  private readonly taskModal = inject<IonicComponentRef>(TASK_MODAL);

  public openNewTaskModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.createNewTaskClicked),
      exhaustMap(async () => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.newTaskModal,
          backdropDismiss: true,
          cssClass: 'new-task-modal',
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map((data: OverlayEventDetail<TaskType>) => {
        if (data.role === 'confirmed' && data.data) {
          return NewTaskModalActions.taskTypeSelected({ taskType: data.data });
        }

        return NewTaskModalActions.closed();
      }),
    );
  });

  public openTaskModalCreate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NewTaskModalActions.taskTypeSelected),
      exhaustMap(async (action) => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.taskModal,
          componentProps: {
            type: action.taskType,
            date: this.planFacade.selectedDate(),
          },
          cssClass: 'task-modal task-only-modal',
          id: 'task-modal',
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map((_data: OverlayEventDetail) => {
        return TaskModalActions.createClosed();
      }),
    );
  });

  public openTaskModalUpdate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PlanPageActions.dayListTaskClicked,
        PlanPageActions.calendarTaskClicked,
        PlanPageActions.todoTaskClicked,
        TaskPageActions.todoTaskClicked,
      ),
      exhaustMap(async (action) => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.taskModal,
          componentProps: {
            type: action.task.type,
            date: this.planFacade.selectedDate(),
            clickedTaskId: action.task.id,
            independent: this.homiSettingsFacade.planningIndependent(),
          },
          cssClass: 'task-modal task-with-subtask-modal',
          id: 'task-modal',
          animated: false,
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map((_data: OverlayEventDetail) => {
        return TaskModalActions.updateClosed();
      }),
    );
  });
}
